import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HiMenuAlt3, HiX, HiChevronDown } from 'react-icons/hi';
import { FiSun, FiMoon, FiShare2 } from 'react-icons/fi';
import { FaPhone, FaWhatsapp, FaHardHat, FaArrowLeft, FaCode } from 'react-icons/fa';
import { useTheme } from '../../context/ThemeContext';
import './Navbar.css';

const SOFTWARE_SECTIONS = ['home', 'about', 'tech', 'sw-gallery', 'contact'];
const CONSTRUCTION_SECTIONS = ['construction', 'gallery', 'contact'];

const Navbar = ({ division, setDivision }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [groupsOpen, setGroupsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(division === 'software' ? 'home' : 'construction');
  const [copied, setCopied] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const groupsRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (groupsRef.current && !groupsRef.current.contains(e.target)) {
        setGroupsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    setActiveSection(division === 'software' ? 'home' : 'construction');
  }, [division]);

  useEffect(() => {
    const sectionIds = division === 'software' ? SOFTWARE_SECTIONS : CONSTRUCTION_SECTIONS;
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      for (let i = sectionIds.length - 1; i >= 0; i--) {
        const el = document.getElementById(sectionIds[i]);
        if (el && el.getBoundingClientRect().top <= 120) {
          setActiveSection(sectionIds[i]);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [division]);

  const navItems = division === 'software'
    ? [
        { id: 'home',       label: 'Home' },
        { id: 'about',      label: 'About' },
        { id: 'tech',       label: 'Services' },
        { id: 'sw-gallery', label: 'Gallery' },
        { id: 'contact',    label: 'Contact' },
      ]
    : [
        { id: 'construction', label: 'Services' },
        { id: 'gallery',      label: 'Gallery' },
        { id: 'contact',      label: 'Contact' },
      ];

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    setMobileOpen(false);
    setGroupsOpen(false);
  };

  const switchToConstruction = () => {
    setDivision('construction');
    setGroupsOpen(false);
    setMobileOpen(false);
  };

  const switchToSoftware = () => {
    setDivision('software');
    setGroupsOpen(false);
    setMobileOpen(false);
  };

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.style.cssText = 'position:fixed;opacity:0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      <motion.nav
        className={`navbar ${scrolled ? 'navbar--scrolled' : ''}`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      >
        <div className="navbar__container">

          {/* Logo */}
          <div className="navbar__logo" onClick={() => division === 'software' ? scrollTo('home') : switchToSoftware()}>
            <span className="navbar__logo-kat">KAT</span>
            <span className="navbar__logo-99">99</span>
          </div>

          {/* Construction back-pill */}
          {division === 'construction' && (
            <button className="navbar__back-btn" onClick={switchToSoftware}>
              <FaArrowLeft />
              <span>Software</span>
            </button>
          )}

          {/* Nav links */}
          <div className="navbar__links">
            {navItems.map((item) => (
              <button
                key={item.id}
                className={`navbar__link ${activeSection === item.id ? 'navbar__link--active' : ''}`}
                onClick={() => scrollTo(item.id)}
              >
                {item.label}
                {activeSection === item.id && (
                  <motion.div
                    className="navbar__link-indicator"
                    layoutId="navIndicator"
                    transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                  />
                )}
              </button>
            ))}

            {/* Our Groups dropdown — only on software view */}
            {division === 'software' && (
              <div className="navbar__groups" ref={groupsRef}>
                <button
                  className={`navbar__link navbar__groups-trigger ${groupsOpen ? 'navbar__link--active' : ''}`}
                  onClick={() => setGroupsOpen(!groupsOpen)}
                >
                  Our Groups
                  <HiChevronDown className={`navbar__groups-chevron ${groupsOpen ? 'navbar__groups-chevron--open' : ''}`} />
                </button>

                <AnimatePresence>
                  {groupsOpen && (
                    <motion.div
                      className="navbar__groups-dropdown"
                      initial={{ opacity: 0, y: -8, scale: 0.97 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -8, scale: 0.97 }}
                      transition={{ duration: 0.18 }}
                    >
                      <div className="navbar__groups-header">KAT99 Group of Companies</div>
                      <button className="navbar__groups-item navbar__groups-item--active" disabled>
                        <div className="navbar__groups-item-icon navbar__groups-item-icon--software">💻</div>
                        <div>
                          <div className="navbar__groups-item-name">KAT99 Software</div>
                          <div className="navbar__groups-item-sub">You are here</div>
                        </div>
                      </button>
                      <button className="navbar__groups-item" onClick={switchToConstruction}>
                        <div className="navbar__groups-item-icon navbar__groups-item-icon--construction">
                          <FaHardHat />
                        </div>
                        <div>
                          <div className="navbar__groups-item-name">KAT99 Construction</div>
                          <div className="navbar__groups-item-sub">Woodworks · Renovations · Building</div>
                        </div>
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </div>

          {/* Icon buttons */}
          <a href="tel:+94761202374" className="navbar__icon-btn navbar__icon-btn--phone" aria-label="Call us">
            <FaPhone />
          </a>
          <a href="https://wa.me/94761202374" target="_blank" rel="noopener noreferrer" className="navbar__icon-btn navbar__icon-btn--whatsapp" aria-label="WhatsApp us">
            <FaWhatsapp />
          </a>

          {/* Share */}
          <div className="navbar__share-wrap">
            <button className="navbar__icon-btn navbar__icon-btn--share" onClick={handleShare} aria-label="Share this page">
              <FiShare2 />
            </button>
            <AnimatePresence>
              {copied && (
                <motion.span className="navbar__share-tooltip"
                  initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 6 }}
                  transition={{ duration: 0.2 }}
                >
                  Link copied!
                </motion.span>
              )}
            </AnimatePresence>
          </div>

          {/* Theme toggle */}
          <button className="navbar__theme-toggle" onClick={toggleTheme} aria-label="Toggle theme">
            <AnimatePresence mode="wait">
              <motion.span
                key={theme}
                initial={{ y: -12, opacity: 0, rotate: -90 }}
                animate={{ y: 0, opacity: 1, rotate: 0 }}
                exit={{ y: 12, opacity: 0, rotate: 90 }}
                transition={{ duration: 0.25 }}
                style={{ display: 'flex' }}
              >
                {theme === 'dark' ? <FiSun /> : <FiMoon />}
              </motion.span>
            </AnimatePresence>
          </button>

          <button className="navbar__mobile-toggle" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Toggle menu">
            {mobileOpen ? <HiX /> : <HiMenuAlt3 />}
          </button>
        </div>
      </motion.nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            className="navbar__mobile-menu"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {navItems.map((item, i) => (
              <motion.button
                key={item.id}
                className={`navbar__mobile-link ${activeSection === item.id ? 'navbar__mobile-link--active' : ''}`}
                onClick={() => scrollTo(item.id)}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
              >
                {item.label}
              </motion.button>
            ))}

            {/* Our Groups in mobile — always show both */}
            <div className="navbar__mobile-groups-wrap">
              <div className="navbar__mobile-groups-label">
                <span className="navbar__mobile-groups-dot" />
                KAT99 Group of Companies
              </div>
              <div className="navbar__mobile-groups-cards">
                <button
                  className={`navbar__mobile-gcard navbar__mobile-gcard--software ${division === 'software' ? 'navbar__mobile-gcard--current' : ''}`}
                  onClick={switchToSoftware}
                  disabled={division === 'software'}
                >
                  <div className="navbar__mobile-gcard-icon navbar__mobile-gcard-icon--software">
                    <FaCode />
                  </div>
                  <div className="navbar__mobile-gcard-body">
                    <span className="navbar__mobile-gcard-name">KAT99 Software</span>
                    <span className="navbar__mobile-gcard-sub">Apps · Web · Digital Solutions</span>
                  </div>
                  {division === 'software' && <span className="navbar__mobile-gcard-badge">Here</span>}
                </button>

                <button
                  className={`navbar__mobile-gcard navbar__mobile-gcard--construction ${division === 'construction' ? 'navbar__mobile-gcard--current' : ''}`}
                  onClick={switchToConstruction}
                  disabled={division === 'construction'}
                >
                  <div className="navbar__mobile-gcard-icon navbar__mobile-gcard-icon--construction">
                    <FaHardHat />
                  </div>
                  <div className="navbar__mobile-gcard-body">
                    <span className="navbar__mobile-gcard-name">KAT99 Construction</span>
                    <span className="navbar__mobile-gcard-sub">Woodworks · Renovations · Building</span>
                  </div>
                  {division === 'construction' && <span className="navbar__mobile-gcard-badge navbar__mobile-gcard-badge--construction">Here</span>}
                </button>
              </div>
            </div>

            <div className="navbar__mobile-actions">
              <a href="tel:+94761202374" className="navbar__mobile-action navbar__mobile-action--phone">
                <FaPhone /><span>+94 76 120 2374</span>
              </a>
              <a href="https://wa.me/94761202374" target="_blank" rel="noopener noreferrer" className="navbar__mobile-action navbar__mobile-action--whatsapp">
                <FaWhatsapp /><span>WhatsApp Us</span>
              </a>
            </div>

            <button className="navbar__mobile-share" onClick={handleShare}>
              <FiShare2 />
              <span>{copied ? 'Link Copied!' : 'Share This Page'}</span>
            </button>

            <motion.button
              className="navbar__mobile-theme"
              onClick={toggleTheme}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.35 }}
            >
              {theme === 'dark' ? <FiSun /> : <FiMoon />}
              <span>{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
